// Card #1652 (design §6.1) — Single entry family, mode types 3 (Ride Intro
// [Detailed]), 4 (During/Mid-Ride [Simple]), 5 (Post-Ride / Score): "one
// horse/rider, fields sized by prominence." Type 5 is the only one with a
// hero element (ScoreHero) — types 3/4 are identity + context only.
//
// riderLocation/horseBio/ownerInfo (type 3 only) are not part of the shared
// component vocabulary (design §6.1 names ClassHeader/EntryIdentity/
// JudgeLine/StartList/RankedList/ScoreHero/PerJudgeBreakout/Banner, not
// these) — they're small per-field readouts local to this one layout,
// each independently omitted when its bundle or a sub-field is absent.
import ClassHeader from "./ClassHeader";
import EntryIdentity from "./EntryIdentity";
import ScoreHero from "./ScoreHero";
import PerJudgeBreakout from "./PerJudgeBreakout";
import Banner from "./Banner";
import { MODE_TYPE } from "./modeTypes";
import { isScorePubliclyVisible } from "../../util/publicScoreVisibility";
import "./SingleEntryLayout.css";

// card #2283 (demo/scoreboard/p4.html, README "Known data trap") — score-person
// rows carry `state` exactly as FVD stored it, mixed-case (Audra Sheffler's
// row is `state: "Fl"`, not `FL`); score-api's riderLocationOf/ownerInfoOf
// pass it through verbatim (scoreboard-mode-payload.js has no case handling).
// Uppercased here, once, at render, rather than in score-api: both callers
// below need the identical fix and this is a presentation concern (how a
// stored abbreviation gets displayed), not a data-shape one — score-api's
// composed payload is unchanged. `null`/`undefined` pass through as-is so a
// missing state still renders as "absent", never the string "undefined".
function upperState(state) {
  return typeof state === "string" ? state.toUpperCase() : state;
}

// card #2283 (demo/scoreboard/p4.html) — the three detail bundles (rider
// location, horse bio, owner info) become labelled columns on a rule instead
// of unlabelled bare paragraphs (README: "nothing on screen says which of
// them is the horse and which is the owner"). Each function below now owns
// its OWN `single-entry__detail` wrapper + label, not just its inner
// paragraph, so a bundle with no data drops its whole column — label
// included — rather than leaving a stray "Rider"/"Horse"/"Owner" caption
// over empty space (card AC3).
function RiderLocation({ riderLocation }) {
  if (!riderLocation) return null;
  const parts = [riderLocation.city, upperState(riderLocation.state), riderLocation.country].filter(Boolean);
  if (!parts.length) return null;
  return (
    <div className="single-entry__detail" data-testid="single-entry-detail-rider">
      <span className="single-entry__detail-label">Rider</span>
      <p className="single-entry__rider-location" data-testid="rider-location">
        {parts.join(", ")}
      </p>
    </div>
  );
}

function HorseBio({ horseBio }) {
  if (!horseBio) return null;
  const { height, breed, foalYear } = horseBio;
  const parts = [];
  if (height != null) parts.push(`${height}h`);
  if (breed) parts.push(breed);
  if (foalYear != null) parts.push(`foaled ${foalYear}`);
  if (!parts.length) return null;
  return (
    <div className="single-entry__detail" data-testid="single-entry-detail-horse">
      <span className="single-entry__detail-label">Horse</span>
      <p className="single-entry__horse-bio" data-testid="horse-bio">
        {parts.join(", ")}
      </p>
    </div>
  );
}

// CHANGE from shipped (p4.html README): the owner's name leads its own line;
// the town underneath is supporting detail and takes a dimmer tier, so the
// two read as two tiers rather than one run-on string. The shipped component
// joined them with an em dash on a single line — that join is removed here,
// not just restyled.
function OwnerInfo({ ownerInfo }) {
  if (!ownerInfo) return null;
  const { name, city, country } = ownerInfo;
  const state = upperState(ownerInfo.state);
  const location = [city, state, country].filter(Boolean).join(", ");
  if (!name && !location) return null;
  return (
    <div className="single-entry__detail" data-testid="single-entry-detail-owner">
      <span className="single-entry__detail-label">Owner</span>
      <p className="single-entry__owner" data-testid="owner-info">
        {name ? (
          <span className="single-entry__owner-name" data-testid="owner-info-name">
            {name}
          </span>
        ) : null}
        {location ? (
          <span className="single-entry__owner-location" data-testid="owner-info-location">
            {location}
          </span>
        ) : null}
      </p>
    </div>
  );
}

// Card #1802 (2026-08-20 spec, item 4) — Post-Ride/Score lists horse name
// separately from the CNO/rider/status line, unlike Ride Intro [Detailed]
// where EntryIdentity bundles all four on one line (design §6.1's shared
// EntryIdentity vocabulary still bundles them as one payload field;
// composeEntryIdentity is untouched — only THIS layout's markup splits
// horseName onto its own line for type 5). Reuses EntryIdentity's own
// `entry-identity-horse` testid/class-name-ish naming (not its `.entry-
// identity__horse` CSS class, which is scoped to EntryIdentity.css and lives
// inside `.entry-identity`'s flex row) since it is still "the horse name
// sub-field of entryIdentity", just relocated by this layout, not a
// different field.
function HorseName({ horseName }) {
  if (!horseName) return null;
  return (
    <p className="single-entry__horse-name" data-testid="entry-identity-horse">
      {horseName}
    </p>
  );
}

// #2002 judge fix 2 -- classifyBoardRenderState (boardRenderState.js) only
// ever screens out a `fields` that is itself falsy; `fields: {}` sails
// through as truthy. Every sub-component here already degrades correctly
// for that shape (ClassHeader/EntryIdentity/ScoreHero each return null when
// none of their own keys are present), but the SUM of all of them returning
// null is a bare `<div class="scoreboard-single-entry">` left on screen --
// the half-rendered board AC5 forbids, and the one case the original
// `if (!fields) throw` (still below, now belt-and-braces on top of this)
// cannot reach because it never fires on a truthy `{}`. This mirrors each
// sub-component's own "hasAnything" gate rather than introducing a new rule,
// so it can only ever agree with what would otherwise render nothing.
// Card #2285 AC2 -- the post-ride screen's ENTIRE right-hand column
// (`.single-entry__result`: ScoreHero, the current-rank rail, and
// PerJudgeBreakout) drops for a ride whose confirmed score is below 55%,
// not just the number inside it -- this is the "no right column on the
// post-ride screen" half of the card, distinct from ClassResultsPage's/
// RankedList's em-dash treatment for the same threshold. A missing
// scoreHero (not yet confirmed) is not a suppression case -- there is no
// score to hide, so the column still renders whatever partial rank/
// breakout data it has, same as before this card. Deliberate product
// decision by Jaybill, 2026-09-04, reaffirmed 2026-09-05 directly on card
// #2285, that runs AGAINST USEF DR123.2 and DR123.3 (docs/rules/usef/
// rulebook-dr-dressage-2026.pdf, current 2026 edition), which both require
// every score and every final result to be published with no low-score
// exception -- see ../../util/publicScoreVisibility.js for the full
// rationale. Render-only: `fields.scoreHero` itself is never touched.
//
// Card #2312 -- a terminated ride's `fields.termReason` (already resolved
// and validated server-side, scoreboard-mode-payload.js's composePostRideScore)
// keeps the right-hand column showing regardless of the below-55 gate above:
// #2285's suppression exists to hide an unexplained low number, but a term
// reason is not a number at all, so there's nothing for that rule to hide --
// ScoreHero renders the outcome word in place of the percentage instead (see
// ScoreHero.jsx). This must be checked BEFORE the scoreHero gate, not after,
// so a terminated ride's composite-0/suppressed-below-55 scoreHero never
// wins the OR and blanks the column a term reason should be filling.
function shouldShowPostRideResult(fields) {
  return (
    !!fields.termReason ||
    fields.scoreHero == null ||
    isScorePubliclyVisible(Number(fields.scoreHero))
  );
}

function hasComposableFields(modeType, fields) {
  const hasClassHeader =
    (!!fields.classNumberName &&
      (fields.classNumberName.classNumber != null || !!fields.classNumberName.className)) ||
    !!fields.testName;
  const identity = fields.entryIdentity || {};
  const hasEntryIdentity =
    identity.competitorNumber != null || !!identity.riderName || !!identity.riderStatus || !!identity.horseName;
  if (hasClassHeader || hasEntryIdentity) return true;

  if (modeType === MODE_TYPE.RIDE_INTRO_DETAILED) {
    return !!fields.riderLocation || !!fields.horseBio || !!fields.ownerInfo;
  }
  if (modeType === MODE_TYPE.POST_RIDE_SCORE) {
    return (
      fields.scoreHero != null || !!fields.currentRank || !!fields.perJudgeBreakout || !!fields.termReason
    );
  }
  return false;
}

export default function SingleEntryLayout({ modeType, fields, banner }) {
  // #2002 AC5 / #1835 — the previous `return null` here was the one shape
  // of #1798's AC1 failure that failed silently: an empty
  // `.scoreboard-content` div, no logo, nothing for either of
  // ScoreboardPage's nested error boundaries to catch. #1835 left the fix
  // as a human call between three options (throw so a boundary catches it;
  // route to BlankLayout directly; make the caller guarantee `fields`).
  // Throwing is picked here: it reuses the ScoreboardErrorBoundary
  // ScoreboardFrame.jsx already wraps every layout family in (the same
  // fallback BlankLayout's own render-error path uses), rather than adding
  // a second, local notion of "blank" or a new coupling to BlankLayout from
  // inside this component. Unreachable in the real pipeline today —
  // classifyBoardRenderState (ScoreboardContent.jsx) already routes a
  // missing `fields` to Blank before this component is ever called — so
  // this is belt-and-braces against a broken caller, not a live path.
  if (!fields || !hasComposableFields(modeType, fields)) {
    throw new Error("SingleEntryLayout rendered without any composable fields");
  }

  const isPostRideScore = modeType === MODE_TYPE.POST_RIDE_SCORE;
  const isRideIntroDetailed = modeType === MODE_TYPE.RIDE_INTRO_DETAILED;
  // card #2283 (demo/scoreboard/p4.html) — the detail rail (its own bordered
  // rule + gap) only renders at all when at least one of the three bundles
  // has something to show; otherwise the identity block would sit above an
  // empty rule with nothing under it. Each of RiderLocation/HorseBio/
  // OwnerInfo still independently decides whether IT renders (its own data
  // shape can be present-but-empty, e.g. `ownerInfo: {}`), so this presence
  // check mirrors hasComposableFields' own top-level shape check rather than
  // duplicating each component's inner field-by-field logic.
  // Gates on RESOLVABILITY, not bundle presence: calling the three
  // components directly (plain functions, no hooks) reuses their own
  // "nothing to show" logic instead of duplicating it, so `{riderLocation:
  // {}, horseBio: {}, ownerInfo: {}}` (present bundles, no fields inside)
  // correctly renders no rail rather than an empty one with a stray border.
  const hasAnyDetail =
    isRideIntroDetailed &&
    [RiderLocation({ riderLocation: fields.riderLocation }), HorseBio({ horseBio: fields.horseBio }), OwnerInfo({ ownerInfo: fields.ownerInfo })].some(Boolean);
  // CHANGE from shipped (p4.html/p5.html) — a modifier class scoped to this
  // ONE mode type, not a shared-file edit: EntryIdentity.css's classes are
  // also used (unstyled by that file's own rules) by the OTHER single-entry
  // mode type through this same layout, so each type's own hierarchy
  // (SingleEntryLayout.css) is scoped under its own modifier rather than
  // touching EntryIdentity.css's shared unqualified rules, same technique
  // #2271 used for RankedList's per-family scoping (RankedList.css's own
  // header comment).
  // Card #2285 followup (judge finding, 2026-09-05) -- when
  // shouldShowPostRideResult is false, `.single-entry__result` never
  // renders, but the root's `grid-template-columns: 1fr 1fr` (below) still
  // reserves its half of the board, so `.single-entry__who` sits in the
  // left column and the right column is empty pale grey -- a suppressed
  // sub-55 score then reads as a half-loaded board rather than an
  // intentional one-column layout. Computed once here (not re-derived in
  // the render branch below) so the class and the render decision can never
  // disagree. `--post-ride-score-single-column` only removes the grid split
  // in SingleEntryLayout.css; it does not center or otherwise restyle
  // `.single-entry__who`, which stays left-aligned exactly as shipped.
  const showPostRideResult = isPostRideScore && shouldShowPostRideResult(fields);
  const rootClassName = isRideIntroDetailed
    ? "scoreboard-single-entry scoreboard-single-entry--ride-intro"
    : isPostRideScore
      ? showPostRideResult
        ? "scoreboard-single-entry scoreboard-single-entry--post-ride-score"
        : "scoreboard-single-entry scoreboard-single-entry--post-ride-score scoreboard-single-entry--post-ride-score-single-column"
      : "scoreboard-single-entry";

  // CHANGE from shipped (demo/scoreboard/p5.html, card #2284) — the shipped
  // layout ran this type as one top-to-bottom stack (identity, then score,
  // then rank/breakout trailing off as small grey text underneath). p5.html
  // splits it into two columns instead: who rode (banner, class header,
  // identity, horse name) on the left in `.single-entry__who`, what they
  // got (score hero, rank rail, per-judge breakout) on the right in
  // `.single-entry__result`, so the score owns half the board rather than
  // sharing a column with everything else. RIDE_INTRO_DETAILED keeps its
  // existing single-column stack below, unchanged.
  if (isPostRideScore) {
    return (
      <div className={rootClassName} data-testid="scoreboard-single-entry">
        <div className="single-entry__who">
          {/* p5.html:410 — "RIDE COMPLETE" leads this screen the same way
              "IN THE RING" leads p4's, via bannerFor's fixed per-modeType
              copy (scoreboard-mode-payload.js). */}
          <Banner banner={banner} />
          <ClassHeader classNumberName={fields.classNumberName} testName={fields.testName} />
          {/* horseName is rendered separately below (item 4 of the spec
              order), not inside the CNO/rider/status line. */}
          <EntryIdentity
            competitorNumber={fields.entryIdentity?.competitorNumber}
            riderName={fields.entryIdentity?.riderName}
            riderStatus={fields.entryIdentity?.riderStatus}
          />
          <HorseName horseName={fields.entryIdentity?.horseName} />
        </div>
        {showPostRideResult ? (
          <div className="single-entry__result">
            <ScoreHero scoreHero={fields.scoreHero} termReason={fields.termReason} />
            {/* design §4.5 — never "place"/"placing" before end-of-class.
                #2002 AC4 — this family never renders a placing value at all
                (RankedList.jsx:149-163 is the one place that "stored placing
                of 0 means unplaced, never index 0" rule applies for real, in
                its own Place column); scoreboard-layout-families.cy.js's
                "single-entry redesign" describe block pins that with a real
                placing-0 row on `fields`.
                Evin, 2026-09-04 (p5.html) — show OVERALL rank AND DIVISION
                rank, not one number: two cells under a single "current rank"
                caption, keeping design §4.5's exact wording intact while
                adding the second figure. Card #2282 landed
                `fields.divisionRank` as the division-scoped sibling to
                `fields.currentRank` (same `{rank, ofCount}` shape); each cell
                is independently omitted when its own field is absent, so a
                class the payload could only rank overall for still renders
                the Overall cell alone rather than a phantom "1 of 1". */}
            {fields.currentRank || fields.divisionRank ? (
              <div className="single-entry__current-rank" data-testid="current-rank">
                <p className="single-entry__current-rank-caption" data-testid="current-rank-caption">
                  current rank
                </p>
                <ul className="single-entry__rank-rail" data-testid="rank-rail">
                  {fields.currentRank ? (
                    <li data-testid="rank-rail-overall">
                      <span className="single-entry__rank-scope">Overall</span>
                      <span className="single-entry__rank-value">
                        <strong>{fields.currentRank.rank}</strong> of {fields.currentRank.ofCount} rides
                      </span>
                    </li>
                  ) : null}
                  {fields.divisionRank ? (
                    <li data-testid="rank-rail-division">
                      <span className="single-entry__rank-scope">Division</span>
                      <span className="single-entry__rank-value">
                        <strong>{fields.divisionRank.rank}</strong> of {fields.divisionRank.ofCount} rides
                      </span>
                    </li>
                  ) : null}
                </ul>
              </div>
            ) : null}
            <PerJudgeBreakout perJudgeBreakout={fields.perJudgeBreakout} />
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className={rootClassName} data-testid="scoreboard-single-entry">
      {/* card #2283 (p4.html:347) — "IN THE RING" leads the Ride Intro
          screen, same Banner component RankedListLayout uses, styled per
          this mode type via .scoreboard-single-entry--ride-intro's own CSS
          override (SingleEntryLayout.css) since p4.html's rule left-aligns
          it here instead of RankedListLayout's centred convention. */}
      {isRideIntroDetailed ? <Banner banner={banner} /> : null}
      <ClassHeader classNumberName={fields.classNumberName} testName={fields.testName} />
      <EntryIdentity {...(fields.entryIdentity || {})} />

      {hasAnyDetail ? (
        <div className="single-entry__details" data-testid="single-entry-details">
          <RiderLocation riderLocation={fields.riderLocation} />
          <HorseBio horseBio={fields.horseBio} />
          <OwnerInfo ownerInfo={fields.ownerInfo} />
        </div>
      ) : null}
    </div>
  );
}
